import React from 'react';
import moment from 'moment';
import { Col, Divider, List, Row, Space } from 'antd';
import { Title } from 'app/components/Typography/Title';
import {
  useGetInspectionDocumentQuery,
  useGetInspectionReportDetailQuery,
  useLazyGetInspectionQuery
} from 'app/features/inspection/api';
import { useNavigate, useParams } from 'react-router';
import {
  CardMenuItem,
  Paper,
  RootContainer,
  Status
} from './components/Elements';
import ExtraInspectionCriteria from './components/ExtraInspectionCriteria';
import OverallInfo from './components/OverallInfo';
import ReportDetailHeader from './components/ReportDetailHeader';
import ESignModal from './components/ESignModal';
import { get, pick } from 'lodash';
import useFetchInspectionProgress from './hooks/useFetchInspectionProgress';
import POItem from '../BookInspection/components/Steps/InspectionCriteria/PoItem';
import classes from './ReportDetail.module.scss';

/**
 * Header Component: Reused From Header Of Job Detail.
 * @returns
 */
const ReportDetail = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [itemConclusion, setItemConclusion] = React.useState('pending');
  const [viewSign, setViewSign] = React.useState(null);
  const { data } = useGetInspectionReportDetailQuery(params.id as any);
  const [fetch, result] = useLazyGetInspectionQuery();
  const { data: documents } = useGetInspectionDocumentQuery({
    inspectionId: params.id as any,
    params: {
      pagination: {
        pageSize: 99999,
        pageNo: 1
      }
    }
  });
  const { data: ProgressData } = useFetchInspectionProgress(
    params.id as string
  );

  const status = React.useMemo(() => {
    return get(ProgressData?.data, 'overallResult');
  }, [ProgressData]);

  const onChangeItemConclusion = (
    status: 'passed' | 'rejected' | 'pending' | 'failed'
  ) => {
    if (itemConclusion !== status) {
      setItemConclusion(status);
    }
  };

  const inspectionDateLabel = React.useMemo(() => {
    if (result.data) {
      const { inspectionDate, inspectionEndDate } = result.data;
      const formattedStartDate = inspectionDate
        ? moment(inspectionDate).format('DD/MM/YYYY')
        : 'N/A';
      const formattedEndDate = inspectionEndDate
        ? moment(inspectionEndDate).format('DD/MM/YYYY')
        : 'N/A';
      return `${formattedStartDate} - ${formattedEndDate}`;
    }
    return 'N/A';
  }, [result]);
  React.useEffect(() => {
    fetch(params.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      {
        // Reuse Header from Job Detail Header.
      }
      <ReportDetailHeader
        title="INSPECTION JOB DETAIL"
        hiddenAssignOnsiteInspection
        onBack={() => {
          navigate(`/dashboard/inspection/job/${params.id}`);
        }}
        overallReportResult={status || itemConclusion}
        status={result?.data?.status || ''}
        jobId={get(result, 'data.jobNoList.jobNo', 'N/A')}
      ></ReportDetailHeader>
      <RootContainer>
        <Row gutter={24} wrap>
          <Col xs={24} sm={24} lg={5}>
            <CardMenuItem>
              <Space align="center">
                Internal Inspection Report
                <Status status={(status || itemConclusion) as any}></Status>
              </Space>
            </CardMenuItem>
          </Col>
          <Col md={24} lg={19}>
            <Paper>
              <Title level={5}>Internal Inspection Report</Title>
              <OverallInfo
                submitDate={
                  data?.submittedDate
                    ? moment(data?.submittedDate).format('DD/MM/YYYY')
                    : 'N/A'
                }
                issuedBy={data?.assignTo ? data?.assignTo.join(', ') : 'N/A'}
                inspectionDate={inspectionDateLabel}
                conclusion={status || 'pending'}
                esign={!!result?.data?.eSignatureDocumentListId}
                onViewEsign={() => {
                  setViewSign({
                    ...(result?.data || {})
                  });
                }}
              ></OverallInfo>
              <Title level={5}>Report Detail :</Title>
              <List
                className={classes.poItems}
                split={false}
                itemLayout="vertical"
                dataSource={data?.inspectionPOItems ?? []}
                renderItem={(record: any, index) => {
                  const { aql } = record;
                  const acceptedAQL = {
                    critical: get(aql, 'critical.acceptanceLevel') || 0,
                    major: get(aql, 'major.acceptanceLevel') || 0,
                    minor: get(aql, 'minor.acceptanceLevel') || 0
                  };
                  return (
                    <List.Item className={classes.item}>
                      <POItem
                        hiddenTemplate
                        value={{
                          ...record,
                          ...record.productProfile,
                          supplierName: get(
                            record?.productProfile || {},
                            'supplier.name',
                            'N/A'
                          ),
                          factoryName: get(
                            record?.productProfile || {},
                            'factory.name',
                            'N/A'
                          ),
                          defectAccepted: {
                            ...acceptedAQL
                          },
                          inspectionAQL: {
                            criticalMinorLevel: record?.criticalMinorLevel,
                            criticalMajorLevel: record?.criticalMajorLevel,
                            criticalLevel: record?.criticalLevel
                          }
                        }}
                        sampleSize={{
                          critical: get(record, 'aql.critical.sampleSize'),
                          major: get(record, 'aql.major.sampleSize'),
                          minor: get(record, 'aql.minor.sampleSize')
                        }}
                        index={index}
                        inspectionRequirement={pick(data || {}, [
                          'criticalLevel',
                          'criticalMajorLevel',
                          'criticalMinorLevel'
                        ])}
                      ></POItem>
                      <div style={{ width: '100%' }}>
                        <Divider></Divider>
                        <ExtraInspectionCriteria
                          progress={ProgressData?.data || []}
                          onChangeConclusion={onChangeItemConclusion}
                          shippingInformation={
                            record.productProfile.shippingInformation
                          }
                          attachments={documents?.list ?? []}
                          poItem={record}
                          isSignSample={record.isSignSample || 0}
                          poItemId={record.id}
                          inspectionCriteria={record.inspectionCriteria}
                          report={get(
                            record,
                            'inspectionCriteria.content',
                            get(record, 'inspectionCriteria.scheme', null)
                          )}
                        ></ExtraInspectionCriteria>
                      </div>
                    </List.Item>
                  );
                }}
              ></List>
            </Paper>
          </Col>
        </Row>
      </RootContainer>
      <ESignModal
        opened={!!viewSign}
        onClose={() => {
          setViewSign(null);
        }}
        signObj={viewSign}
      />
    </div>
  );
};

export default ReportDetail;
